import React, { useEffect, useState } from "react";
import TopBar from "../../components/TopBar";
import EmptyState from "../../components/EmptyState";
import { Modal, Field } from "../Fields";
import { api, fmtUSD, todayISO } from "../../lib/api";
import { ClipboardList, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "received", label: "Received" },
  { value: "in_progress", label: "In Progress" },
  { value: "awaiting_parts", label: "Awaiting Parts" },
  { value: "done", label: "Done" },
  { value: "delivered", label: "Delivered" },
];

const STATUS_COLORS = {
  received: "bg-[#F5F4F0] text-[#66645E]",
  in_progress: "bg-[#1F1E1B] text-white",
  awaiting_parts: "bg-[#BC4727] text-white",
  done: "bg-[#1A4135] text-white",
  delivered: "bg-[#1A4135] text-white",
};

const blank = {
  customer_id: "", vehicle: "", vin: "", complaint: "", diagnosis: "",
  status: "received", labor_hours: 0, labor_rate: 95, parts_cost: 0,
  received_date: "", completed_date: "", notes: "", is_internal: false,
};

export default function WorkOrders({ businessId }) {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const [r, c] = await Promise.all([
      api.get(`/work-orders?business_id=${businessId}`),
      api.get(`/customers`),
    ]);
    setRows(r.data); setCustomers(c.data);
  };

  useEffect(() => { load(); }, [businessId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, business_id: businessId,
      labor_hours: parseFloat(form.labor_hours) || 0,
      labor_rate: parseFloat(form.labor_rate) || 0,
      parts_cost: parseFloat(form.parts_cost) || 0,
    };
    try {
      if (editing) await api.put(`/work-orders/${editing.id}`, payload);
      else await api.post(`/work-orders`, payload);
      toast.success(editing ? "Work order updated" : "Work order created");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch { toast.error("Could not save work order"); }
  };

  const onEdit = (r) => { setEditing(r); setForm({ ...blank, ...r }); setOpen(true); };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this work order?")) return;
    await api.delete(`/work-orders/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <TopBar
        title="Work Orders"
        subtitle="Track each repair job from drop-off to delivery."
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button data-testid="add-work-order-btn" onClick={() => { setEditing(null); setForm({ ...blank, received_date: todayISO() }); setOpen(true); }} className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#A03B1F] flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Work Order
            </button>
          </div>
        }
      />

      {rows.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No work orders yet" hint="Log your first repair job when a customer drops off." testid="empty-work-orders" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="work-orders-table">
            <thead>
              <tr>
                <th>Received</th>
                <th>Customer</th>
                <th>Vehicle</th>
                <th>Complaint</th>
                <th>Status</th>
                <th className="num">Labor</th>
                <th className="num">Parts</th>
                <th className="num">Total</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const laborTotal = (r.labor_hours || 0) * (r.labor_rate || 0);
                const total = laborTotal + (r.parts_cost || 0);
                return (
                  <tr key={r.id} data-testid={`wo-row-${r.id}`}>
                    <td className="tabular">{r.received_date}</td>
                    <td>{r.customer_name || "—"}</td>
                    <td className="font-semibold">{r.vehicle}</td>
                    <td className="text-[#66645E]">{r.complaint}</td>
                    <td><span className={`text-[10px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 ${STATUS_COLORS[r.status]}`}>{r.status.replace("_", " ")}</span></td>
                    <td className="num">{r.labor_hours}h · {fmtUSD(laborTotal)}</td>
                    <td className="num">{fmtUSD(r.parts_cost)}</td>
                    <td className="num font-bold">{fmtUSD(total)}</td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`edit-wo-${r.id}`} onClick={() => onEdit(r)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`delete-wo-${r.id}`} onClick={() => onDelete(r.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Work Order" : "New Work Order"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="wo-form">
            <label className="flex items-start gap-2 bg-[#FAF7EC] border-2 border-[#1F1E1B] p-3 cursor-pointer" data-testid="wo-internal-toggle">
              <input
                type="checkbox"
                data-testid="wo-internal-checkbox"
                checked={!!form.is_internal}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm({
                    ...form,
                    is_internal: checked,
                    customer_id: checked ? `internal_farm_${businessId}` : "",
                  });
                }}
                className="mt-1"
              />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider">Internal work for the Farm</div>
                <div className="text-[11px] text-[#66645E] mt-0.5">
                  Flags this job as work done for the Farm. When you invoice it, a matching Farm expense
                  is auto-created so the Farm books reflect what this job would have cost to outsource.
                </div>
              </div>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Customer" required>
                <select data-testid="wo-customer-select" required disabled={!!form.is_internal} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className={`w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm ${form.is_internal ? "opacity-60" : ""}`}>
                  <option value="">— Select customer —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.name} (${c.company})` : c.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="wo-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Vehicle" required>
                <input data-testid="wo-vehicle-input" required value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} placeholder="2018 Peterbilt 389" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="VIN / Serial">
                <input data-testid="wo-vin-input" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Complaint" required>
              <textarea data-testid="wo-complaint-input" rows={2} required value={form.complaint} onChange={(e) => setForm({ ...form, complaint: e.target.value })} placeholder="What the customer says is wrong" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="Diagnosis">
              <textarea data-testid="wo-diagnosis-input" rows={2} value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} placeholder="What you found" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Labor Hours">
                <input data-testid="wo-labor-hours-input" type="number" step="0.25" value={form.labor_hours} onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Labor Rate $/hr">
                <input data-testid="wo-labor-rate-input" type="number" step="0.01" value={form.labor_rate} onChange={(e) => setForm({ ...form, labor_rate: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Parts Cost">
                <input data-testid="wo-parts-cost-input" type="number" step="0.01" value={form.parts_cost} onChange={(e) => setForm({ ...form, parts_cost: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Received Date">
                <input data-testid="wo-received-date" type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Completed Date">
                <input data-testid="wo-completed-date" type="date" value={form.completed_date} onChange={(e) => setForm({ ...form, completed_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="wo-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="wo-cancel-btn" onClick={() => setOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="wo-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
